// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextField } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';

import { ErrorLabel } from '../../commons';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useOpenedIncomingPayment } from '@tectonics/prs/incoming-payment';
import { getSafeEWalletPaymentOption } from '@tectonics/prs/incoming-payment/payment-option';
import { Option, Select } from '@tectonics/look-and-feel/shopify/commons/atoms';
import { getSafePaymentOptionsV2 } from '@tectonics/prs/incoming-payment/get-payment-options';

export function InputNigerianBankList(): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();

  const [value, handleChange] = useUserInputAsTextField('selectedBankCode');

  const validationError = useUserInputValidationError('selectedBankCode');
  const formHasBeenSent = useFormHasBeenSent();

  const incomingPayment = useOpenedIncomingPayment();

  const paymentOptions = getSafePaymentOptionsV2(incomingPayment);
  const bankList = getSafeEWalletPaymentOption(paymentOptions);

  return (
    <div data-test-name="customer-bank" style={{ gridColumn: '1/3' }}>
      <label htmlFor={inputId}>Bank</label>
      <Select
        id={inputId}
        value={value}
        onChange={handleChange}
        readOnly={submitting}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        name="customer-bank"
      >
        <Option value="">Select your bank</Option>
        {bankList.map(({ name, value }) => {
          return (
            <Option key={name} value={value}>
              {name}
            </Option>
          );
        })}
      </Select>

      {formHasBeenSent && validationError && (
        <ValidationMessage validationError={validationError} />
      )}
    </div>
  );
}

type ValidationMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationMessage({ validationError }: ValidationMessageProps): Node {
  switch (validationError.id) {
    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return (
        <ErrorLabel>
          Invalid bank
        </ErrorLabel>
      );
  }
}
